import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {useOnMount} from 'shared/web';

import {
  getConfiguredFinancialProducts,
  getFinancialProductOffer,
  toFinancialProductPreferenceCoordinates,
} from 'features/financial-products';

import {
  FinancialProductPreferenceCoordinates,
  FinancialProductType,
} from '../../api/types/onlineOfferTypes';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {FinancialProductPreferenceOutbox} from './FinancialProductPreferenceOutbox';
import {hasChoosableConfiguration} from './preference/hasChoosableConfiguration';
import {hasRebasedStoredSelection} from './preference/hasRebasedStoredSelection';
import {hasRecoveredFromRefusal} from './preference/hasRecoveredFromRefusal';
import {usePreferenceWrite} from './preference/usePreferenceWrite';

const FINANCIAL_PRODUCT_ORDER = ['STANDARD', 'BALLOON', 'LEASING'] as const;

/** The customer's durable product choice; the write's answer is the quote for it. */
export function useFinancialProductPreference() {
  const {financialProducts, financialProductsFulfilledAt, address, refresh} =
    useFinancialProducts();
  const restoredEntry = useRef(
    isNotNil(address) ? FinancialProductPreferenceOutbox.read(address) : null
  ).current;
  const [openedProduct, setOpenedProduct] = useState<FinancialProductType | null>(
    restoredEntry?.coordinates.productType ?? null
  );
  const hasAuthoritativeClosedRead = financialProducts?.mode === 'CONTRACT_SNAPSHOT';
  const isActiveOwner = useRef(false);
  const {
    products,
    refusalForState,
    refusal,
    isRefusalCurrent,
    heldConflictNotice,
    draft,
    quoteLifecycle,
    updateDraft,
    queuePreferenceWrite,
    startWriteOwnership,
    clearCurrentWriteAttempt,
    finishWriteOwnership,
    retireDraftForClosedSnapshot,
    sendRestoredEntryOnceRead,
  } = usePreferenceWrite({
    address,
    financialProducts,
    financialProductsFulfilledAt,
    hasAuthoritativeClosedRead,
    isActiveOwner,
    restoredEntry,
    refresh,
    setOpenedProduct,
  });

  // This must follow the write hook so a restored intent is sent once ownership starts.
  useOnMount(
    () => {
      isActiveOwner.current = true;
      startWriteOwnership();

      if (hasAuthoritativeClosedRead) {
        retireDraftForClosedSnapshot();
        return;
      }

      sendRestoredEntryOnceRead();
    },
    () => {
      isActiveOwner.current = false;
      clearCurrentWriteAttempt();
      finishWriteOwnership();
    }
  );

  const displayedProducts = hasAuthoritativeClosedRead ? financialProducts : products;
  const isReadOnly = displayedProducts?.mode === 'CONTRACT_SNAPSHOT';
  const preferredOpenedProduct =
    isReadOnly && isNotNil(draft) ? displayedProducts?.selectedProduct : openedProduct;

  // Keep stored, opened, or still-choosable unavailable products from disappearing mid-flow.
  const configuredOffers = isNotNil(displayedProducts)
    ? getConfiguredFinancialProducts(displayedProducts, FINANCIAL_PRODUCT_ORDER).map(
        (productType) => getFinancialProductOffer(displayedProducts, productType)
      )
    : [];
  const offers = configuredOffers
    .filter(isNotNil)
    .filter(
      (offer) =>
        offer.availability === 'AVAILABLE' ||
        hasChoosableConfiguration(offer) ||
        offer.productType === displayedProducts?.selectedProduct ||
        offer.productType === preferredOpenedProduct
    );
  const openedOffer =
    offers.find((offer) => offer.productType === preferredOpenedProduct) ??
    offers.find((offer) => offer.productType === displayedProducts?.selectedProduct) ??
    offers.at(0);

  const opened = match(openedOffer)
    .with(Pattern.nullish, () => null)
    .otherwise((offer) => ({
      offer,
      coordinates:
        !isReadOnly && draft?.productType === offer.productType
          ? draft
          : toFinancialProductPreferenceCoordinates(offer),
    }));

  const commit = (coordinates: FinancialProductPreferenceCoordinates) => {
    // A rebased selection is only a local fallback; confirming it must still write.
    if (
      openedOffer?.productType === coordinates.productType &&
      !hasRebasedStoredSelection(openedOffer) &&
      equals(opened?.coordinates, coordinates)
    ) {
      return;
    }
    updateDraft(coordinates);
    queuePreferenceWrite(coordinates);
  };

  const openProduct = (productType: FinancialProductType) => {
    const offer = offers.find((candidate) => candidate.productType === productType);

    setOpenedProduct(productType);

    if (isNotNil(offer) && !isReadOnly) {
      commit(toFinancialProductPreferenceCoordinates(offer));
    }
  };

  // An availability refusal expires once a later poll can describe the opened product again.
  const currentRefusal =
    !isRefusalCurrent && hasRecoveredFromRefusal(refusalForState, openedOffer) ? null : refusal;
  // A conflict notice outlives the refused mutation state while its rebased write settles.
  const visibleRefusal = currentRefusal ?? heldConflictNotice?.refusal ?? null;
  const storedOpenedOffer =
    isNotNil(products) && isNotNil(openedOffer)
      ? getFinancialProductOffer(products, openedOffer.productType)
      : undefined;

  return {
    products: displayedProducts,
    offers,
    refusal: visibleRefusal,
    rebased: hasRebasedStoredSelection(storedOpenedOffer),
    isReadOnly,
    opened,
    quoteLifecycle: isReadOnly ? 'ready' : quoteLifecycle,
    commit,
    openProduct,
  };
}
